import { ExtraErrorData } from '@sentry/integrations';
import * as Sentry from '@sentry/react';
import { jsx } from '@emotion/react/jsx-runtime';
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { helper } from '@myrealtrip/frontend-weblog';

/**
 * Connect to Sentry Project using SENTRY_DSN
 * Read SENTRY_DSN from .env
 * @see {@link https://docs.sentry.io/platforms/javascript/guides/react/configuration/}
 * @example
 * ```js
 * import { sentry } from '@myrealtrip/log';
 *
 * sentry.init({
 *   initialScope: {
 *     tags: { "my-tag": "my value" },
 *     user: { id: 42, email: "john.doe@example.com" },
 *   }
 * });
 * ReactDOM.render(<App />, document.getElementById('app'));
 * ```
 */
var sentry = {
    init: function (sentryOptions) {
        /**
         * const { SENTRY_DSN, TARGET_ENV } = process.env;
         * 위처럼 하면
         * var _a = process.env, SENTRY_DSN = _a.SENTRY_DSN, TARGET_ENV = _a.TARGET_ENV;
         * 이렇게 변환이 되어 var _a = process.env 선언시점에 process가 존재하지 않는다
         * 그래서 아래처럼 선언하여 init이 실행시점에 process를 가져올 수 있게 한다.
         */
        var SENTRY_DSN = process.env.SENTRY_DSN || process.env.NEXT_PUBLIC_SENTRY_DSN;
        var TARGET_ENV = process.env.TARGET_ENV || process.env.NEXT_PUBLIC_TARGET_ENV;
        if (!SENTRY_DSN) {
            console.warn('센트리를 사용하기 위해 DSN 지정이 필요합니다. 환경 변수 SENTRY_DSN 을 설정해주세요.');
            return;
        }
        var initialScope = (sentryOptions !== null && sentryOptions !== void 0 ? sentryOptions : {}).initialScope;
        Sentry.init({
            dsn: SENTRY_DSN,
            environment: TARGET_ENV,
            sampleRate: 1.0,
            maxBreadcrumbs: 50,
            initialScope: initialScope,
            ignoreErrors: ['ScriptExternalLoadError'],
            integrations: [
                // to wraps native APIs to capture breadcrumbs.
                // By default, the Sentry SDK wraps all APIs.
                new Sentry.Integrations.Breadcrumbs({
                    console: true,
                    dom: true,
                    fetch: true,
                    history: true,
                    sentry: true,
                    xhr: false,
                }),
                // to deduplicates certain events.
                new Sentry.Integrations.Dedupe(),
                // to wraps native time and events APIs
                // (setTimeout, setInterval, requestAnimationFrame, addEventListener/removeEventListener)
                // in try/catch blocks to handle async exceptions.
                new Sentry.Integrations.TryCatch(),
                // to attach HTTP request information,
                // such as URL, user-agent, referrer, and other headers to the event.
                new Sentry.Integrations.HttpContext(),
                // to extracts all non-native attributes from the error object
                // and attaches them to the event as the extra data.
                // If the error object has a .toJSON() method,
                // the ExtraErrorData integration will run it to extract additional information.
                new ExtraErrorData(),
            ],
            beforeSend: function (event) {
                var _a, _b, _c, _d, _e;
                var _f = ((_a = event.request) !== null && _a !== void 0 ? _a : {}).headers, headers = _f === void 0 ? {} : _f;
                var _g = ((_d = (_c = (_b = event.exception) === null || _b === void 0 ? void 0 : _b.values) === null || _c === void 0 ? void 0 : _c[0]) !== null && _d !== void 0 ? _d : {}).type, type = _g === void 0 ? '' : _g;
                var tags = Object.keys((_e = event.tags) !== null && _e !== void 0 ? _e : {});
                if (type.match(/SendBirdException/) ||
                    Object.entries(headers).some(function (_a) {
                        var key = _a[0], value = _a[1];
                        return key === 'User-Agent' && value.trim().match(/Yeti/);
                    }) ||
                    tags.includes('mrt_error_platform')) {
                    return null;
                }
                return event;
            },
        });
    },
    capture: function (_a) {
        var error = _a.error, level = _a.level, _b = _a.tags, tags = _b === void 0 ? {} : _b;
        Sentry.withScope(function (scope) {
            scope.setLevel(level);
            scope.setTags(tags);
            Sentry.captureException(error, { extra: { raw: error } });
        });
    },
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

var DEFAULT_THRESHOLD = 0.5;
var DEFAULT_DELAY = 200;
/**
 * ref에 담긴 HTML 요소가 화면 안으로 들어오면 인자로 받은 이벤트 핸들러들을 알맞게 호출합니다.
 *
 *
 * @example
 * ```tsx
 * const impressionRef = useImpression({
 *   onImpressionStart: () => console.log('div 노출!'),
 *   onImpressionEnd: () => console.log('div 숨음!'),
 * });
 *
 * return <div ref={impressionRef} />
 * ```
 */
var TIMER;
function useImpression(_a) {
    var onImpressionStart = _a.onImpressionStart, onImpressionEnd = _a.onImpressionEnd, _b = _a.rootMargin, rootMargin = _b === void 0 ? 0 : _b, _c = _a.threshold, threshold = _c === void 0 ? DEFAULT_THRESHOLD : _c, _d = _a.delay, delay = _d === void 0 ? DEFAULT_DELAY : _d;
    var _e = useState(false), isImpressed = _e[0], setImpressed = _e[1];
    var handleImpressionStart = useCallback(function () {
        if (isImpressed === true) {
            return;
        }
        TIMER = setTimeout(function () {
            onImpressionStart === null || onImpressionStart === void 0 ? void 0 : onImpressionStart();
        }, delay);
        setImpressed(true);
    }, [delay, isImpressed, onImpressionStart]);
    var handleImpressionEnd = useCallback(function () {
        if (isImpressed === false) {
            return;
        }
        clearTimeout(TIMER);
        onImpressionEnd === null || onImpressionEnd === void 0 ? void 0 : onImpressionEnd();
        setImpressed(false);
    }, [isImpressed, onImpressionEnd]);
    var handleImpression = useCallback(function (entries) {
        var entry = entries[0];
        var currentRatio = entry.intersectionRatio;
        var isHide = threshold === 0 ? !entry.isIntersecting : currentRatio < threshold;
        if (isHide) {
            handleImpressionEnd();
        }
        else {
            handleImpressionStart();
        }
    }, [handleImpressionEnd, handleImpressionStart, threshold]);
    var intersectionObserver = useMemo(function () {
        if (typeof IntersectionObserver === 'undefined') {
            return undefined;
        }
        return new IntersectionObserver(handleImpression, {
            rootMargin: "".concat(rootMargin, "px"),
            threshold: threshold,
        });
    }, [handleImpression, rootMargin, threshold]);
    var ref = useCallback(function (element) {
        if (intersectionObserver === undefined) {
            return;
        }
        intersectionObserver.disconnect();
        if (element !== null) {
            intersectionObserver.observe(element);
        }
    }, [intersectionObserver]);
    return ref;
}

var logHelpers = {
    click: helper.sendClickLog,
    pageview: helper.sendPageViewLog,
    init: helper.sendInitLog,
    layerview: helper.sendLayerViewLog,
    performance: helper.sendPerformanceLog,
    search: helper.sendSearchLog,
    impression: helper.sendImpressionLog,
};
var shouldSendLogOnce = ['pageview', 'layerview'];
/**
 * 로그 전송시 콜백을 등록할 수 있는 커넥터 반환 커스텀 훅
 * @params eventType - 커넥터를 등록할 로그의 이벤트 타입
 * @params impressionKey - 이벤트 타입이 impression인 경우 필요한 키값
 * @returns connector - 커넥터는 인자로 받은 logData의 Promise를 반환하므로 sendLog를 resolved 콜백에서 연결할 수 있다.
 * @returns helper - 파라미터로 넘긴 eventType에 매칭되는 로그 전송 helper
 * @example
 * ```ts
  const [connector, sendClickLog] = useLogConnector('click');

  const handleClick = () => {
    connector(
      logData,
      callback,
      { connectStreamType: 'after' } // callback이 로그 전송 후 실행됨
    ).then(sendClickLog);
  };
 * ```
 */
function useLogConnector(_a) {
    var _this = this;
    var eventType = _a.eventType, impressionKey = _a.impressionKey;
    // connector를 연결할 때 disconnector에 저장한다. 저장된 내용이 있다면 최초 1회 로그 전송이 완료된 상태.
    var disconnector = useRef();
    var isSendLogOnce = useCallback(function (sendLogOnce) {
        return shouldSendLogOnce.includes(eventType) || Boolean(sendLogOnce);
    }, [eventType]);
    var connector = useCallback(function (logData, callback, options) { return __awaiter(_this, void 0, void 0, function () {
        var _a, _b, sendLogOnce, _c, disableConnect, _d, connectStreamType, _e;
        return __generator(this, function (_f) {
            switch (_f.label) {
                case 0:
                    _a = options !== null && options !== void 0 ? options : {}, _b = _a.sendLogOnce, sendLogOnce = _b === void 0 ? false : _b, _c = _a.disableConnect, disableConnect = _c === void 0 ? false : _c, _d = _a.connectStreamType, connectStreamType = _d === void 0 ? 'before' : _d;
                    // 1. 함수 미실행 조건: disableConnect 옵션값이 true인 경우
                    if (disableConnect)
                        return [2 /*return*/];
                    // 2. 함수 미실행 조건: 최초 1회 조건에 해당하면서 disconnector가 존재할 때(=이미 로그가 전송된 경우)
                    if (isSendLogOnce(sendLogOnce) && disconnector.current)
                        return [2 /*return*/];
                    // 3. 함수 미실행 조건: 이벤트 타입이 impression인데 키값 인자가 없는 경우
                    if (eventType === 'impression' && !impressionKey)
                        return [2 /*return*/];
                    if (!!disconnector.current) return [3 /*break*/, 2];
                    _e = disconnector;
                    return [4 /*yield*/, helper.connect(function (logResponse, error) {
                            // 등록된 콜백함수는 모든 로깅시 실행되므로, 실행 전 특정 이벤트 조건을 먼저 검사
                            // TODO(weblog): performance 등 eventName이 커스텀되는 경우가 있어 이벤트 추가시 조건문 보완 필요 (고유키 props 등)
                            var resEventName = logResponse.event_name, resEventType = logResponse.event_type;
                            if ((logData.event_name || logData.eventName) !== resEventName ||
                                eventType !== resEventType)
                                return;
                            callback(logResponse, error);
                        }, connectStreamType)];
                case 1:
                    _e.current = _f.sent();
                    _f.label = 2;
                case 2: return [2 /*return*/, logData];
            }
        });
    }); }, [eventType, impressionKey, isSendLogOnce]);
    // disconnector에 저장된 connector를 비움
    var resetDisconnector = useCallback(function () {
        if (disconnector.current) {
            disconnector.current();
            disconnector.current = undefined;
        }
    }, []);
    var sendLog = useCallback(function (logData) { return __awaiter(_this, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!logData)
                        return [2 /*return*/];
                    return [4 /*yield*/, ((_a = logHelpers[eventType]) === null || _a === void 0 ? void 0 : _a.call(logHelpers, logData, impressionKey !== null && impressionKey !== void 0 ? impressionKey : ''))];
                case 1:
                    _b.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [eventType, impressionKey]);
    // Unmount 시, resetDisconnector() 실행
    useEffect(function () {
        return function () { return resetDisconnector(); };
    }, [resetDisconnector]);
    // 페이지 떠날 시, resetDisconnector() 실행 (location.href 등)
    if (typeof window !== 'undefined') {
        window.onpagehide = resetDisconnector;
    }
    return [connector, sendLog];
}

var sendImpressionLog$1 = helper.sendImpressionLog;
var useChargedImpressionLog = function (_a) {
    var chargedImpressionOptions = _a.chargedImpressionOptions, screenName = _a.screenName, eventName = _a.eventName, pageCategory = _a.pageCategory, params = _a.params, _b = _a.threshold, threshold = _b === void 0 ? 0.5 : _b, _c = _a.delay, delay = _c === void 0 ? 0 : _c, _d = _a.isDisabled, isDisabled = _d === void 0 ? false : _d;
    var combinedParams = __assign({ screenName: screenName, eventName: eventName, pageCategory: pageCategory }, params);
    var handleImpression = function (isImpressed) {
        if (isDisabled)
            return;
        var key = chargedImpressionOptions.key, chargeOptions = __rest(chargedImpressionOptions, ["key"]);
        sendImpressionLog$1(combinedParams, key, __assign({ isCharging: isImpressed }, chargeOptions));
    };
    var impressionRef = useImpression({
        onImpressionStart: function () { return handleImpression(true); },
        onImpressionEnd: function () { return handleImpression(false); },
        threshold: threshold,
        delay: delay,
    });
    return { impressionRef: impressionRef };
};

/**
 * 요소가 화면 안에 등장하거나 화면에서 사라지면 onImpressionStart, onImpressionEnd 이벤트를 트리거하는 컴포넌트입니다.
 *
 * @example
 * ```tsx
 * <ImpressionArea onImpressionStart={() => console.log('나타났다!')}>
 *   <div />
 * </ImpressionArea>
 * ```
 */
var ImpressionArea = function (_a) {
    var children = _a.children, className = _a.className, css = _a.css, rest = __rest(_a, ["children", "className", "css"]);
    var impressionRef = useImpression(__assign({}, rest));
    return (jsx("div", { ref: impressionRef, className: className, css: css, children: children }));
};

// 기본 5KB 크기 제한 (바이트 단위)
var DEFAULT_MAX_DATA_SIZE_BYTES = 5 * 1024; // 5KB
/**
 * 최대 데이터 크기 반환 함수
 */
function getMaxDataSizeBytes() {
    var _a;
    var customSize = parseInt(((_a = process === null || process === void 0 ? void 0 : process.env) === null || _a === void 0 ? void 0 : _a.NEXT_PUBLIC_WEBLOG_MAX_SIZE_BYTES) || '0', 10);
    return customSize > 0 ? customSize : DEFAULT_MAX_DATA_SIZE_BYTES;
}
/**
 * 데이터 크기 검증 기능 활성화 여부 반환 함수
 * 기본값: true (활성화)
 * 환경변수로 false 설정 시 비활성화
 */
function isValidationEnabled() {
    var _a;
    return ((_a = process === null || process === void 0 ? void 0 : process.env) === null || _a === void 0 ? void 0 : _a.NEXT_PUBLIC_WEBLOG_SIZE_VALIDATION_ENABLED) !== 'false';
}
/**
 * 객체의 바이트 크기 계산
 * @param data 크기를 측정할 데이터
 * @returns 바이트 단위의 크기
 */
function getDataSizeInBytes(data) {
    try {
        var jsonString = JSON.stringify(data);
        // TextEncoder를 사용하여 정확한 바이트 크기 계산 (UTF-8 인코딩 고려)
        return new TextEncoder().encode(jsonString).length;
    }
    catch (error) {
        console.error('Failed to calculate data size:', error);
        return 0;
    }
}
/**
 * 바이트 단위 변환 반환 함수
 * @param bytes 바이트 크기
 * @returns 포맷된 크기 문자열
 */
function formatBytes(bytes) {
    if (bytes === 0)
        return '0 Bytes';
    var k = 1024;
    var sizes = ['Bytes', 'KB', 'MB'];
    var i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
/**
 * 데이터 크기 검증 결과를 반환 함수
 * @param data 검증할 데이터
 * @returns 검증 결과 객체
 */
function validateDataSize(data) {
    var sizeInBytes = getDataSizeInBytes(data);
    var maxSizeBytes = getMaxDataSizeBytes();
    var isValid = sizeInBytes <= maxSizeBytes;
    return {
        isValid: isValid,
        sizeInBytes: sizeInBytes,
        formattedSize: formatBytes(sizeInBytes),
        maxSizeFormatted: formatBytes(maxSizeBytes),
        maxSizeBytes: maxSizeBytes,
    };
}

var sendClickLog = helper.sendClickLog, sendImpressionLog = helper.sendImpressionLog, sendPageViewLog = helper.sendPageViewLog, sendSearchLog = helper.sendSearchLog;
var Logger = /** @class */ (function () {
    function Logger() {
    }
    Logger.prototype.validateAndNotify = function (eventType, params) {
        if (!isValidationEnabled()) {
            return true;
        }
        var validation = validateDataSize(params);
        if (!validation.isValid) {
            console.warn("[Logger] Data size exceeded for ".concat(eventType, " event. Size: ").concat(validation.formattedSize, ", Max: ").concat(validation.maxSizeFormatted), params);
            var error = new Error("Weblog data size exceeded: ".concat(eventType, " event"));
            error.name = '🚨WeblogDataSizeExceeded🚨';
            sentry.capture({
                error: error,
                level: 'error',
                tags: {
                    eventType: eventType,
                    eventName: params.eventName || 'unknown',
                    screenName: params.screenName || 'unknown',
                    dataSize: validation.formattedSize,
                    maxSize: validation.maxSizeFormatted,
                },
            });
            return false;
        }
        return true;
    };
    Logger.prototype.getView = function () {
        var _this = this;
        return function (params) {
            var isValid = _this.validateAndNotify('pageView', params);
            if (isValid) {
                sendPageViewLog(params);
            }
        };
    };
    Logger.prototype.getClick = function () {
        var _this = this;
        return function (params) {
            var isValid = _this.validateAndNotify('click', params);
            if (isValid) {
                sendClickLog(__assign({}, params));
            }
        };
    };
    Logger.prototype.getImpression = function () {
        var _this = this;
        return function (key, params, chargeOptions) {
            var isValid = _this.validateAndNotify('impression', params);
            if (isValid) {
                sendImpressionLog(params, key, chargeOptions);
            }
        };
    };
    Logger.prototype.getCustomEvent = function () {
        var _this = this;
        return function (eventType, params) {
            var isValid = _this.validateAndNotify(eventType, params);
            if (isValid) {
                if (eventType === 'search') {
                    sendSearchLog(params);
                }
            }
        };
    };
    Logger.prototype.getPageLogger = function () {
        return {
            pageView: this.getView(),
            click: this.getClick(),
            impression: this.getImpression(),
            event: this.getCustomEvent(),
        };
    };
    return Logger;
}());
var instance = new Logger();

function isDefinedImpressionLoggerType(loggerType) {
    return loggerType === 'impression';
}
function isDefinedLoggerType(loggerType) {
    return loggerType === 'click' || loggerType === 'pageView';
}
/**
 * logger를 선언적으로 사용할 수 있는 컴포넌트입니다. nodeEventName과 일치하는 자식 컴포넌트의 프로퍼티에 이벤트를 발송하는 로직을 주입합니다.
 *
 * @example
 * <LoggingEvent
 *   screenName="offer_detail"
 *   eventName="offer"
 *   pageCategory="페이지 이름"
 *   nodeEventName="onClick"
 *   params={{ 서비스: service.name }}
 * >
 *   <button onClick={handleClick}>구독 취소</button>
 * </LoggingEvent>
 */
function LoggingEvent(_a) {
    var _b;
    var screenName = _a.screenName, eventName = _a.eventName, pageCategory = _a.pageCategory, nodeEventName = _a.nodeEventName, params = _a.params, loggerType = _a.loggerType, impressionOption = _a.impressionOption, children = _a.children;
    useEffect(function () {
        React.Children.only(children);
    }, [children]);
    return React.cloneElement(children, (_b = {},
        _b[nodeEventName] = function () {
            var _a;
            var _b, _c;
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            (_c = (_b = children.props)[nodeEventName]) === null || _c === void 0 ? void 0 : _c.call.apply(_c, __spreadArray([_b], args, false));
            var pageLogger = instance.getPageLogger();
            var combinedParams = __assign({ screenName: screenName, eventName: eventName, pageCategory: pageCategory }, params);
            if (isDefinedImpressionLoggerType(loggerType) && (impressionOption === null || impressionOption === void 0 ? void 0 : impressionOption.key)) {
                pageLogger[loggerType](impressionOption.key, combinedParams);
            }
            if (isDefinedLoggerType(loggerType)) {
                pageLogger[loggerType](combinedParams);
            }
            else {
                pageLogger.event(loggerType, combinedParams);
            }
            if (children.props[nodeEventName] != null && children.props[nodeEventName] === 'function') {
                return (_a = children.props)[nodeEventName].apply(_a, args);
            }
        },
        _b));
}

/**
 * logger를 선언적으로 사용할 수 있는 컴포넌트입니다.
 * 자식 요소에서 onClick 이벤트가 발생하면 로거의 click 메소드를 사용하여 이벤트 로그를 발송합니다.
 *
 * @example
 * <LoggingClick
 *   screenName="offer_detail"
 *   eventName="offer_detail"
 *   pageCategory="페이지 이름"
 *   params={{ 서비스: service.name }}
 * >
 *   <button>구독 취소</button>
 * </LoggingClick>
 *
 *
 */
function LoggingClick(_a) {
    var children = _a.children, rest = __rest(_a, ["children"]);
    return (jsx(LoggingEvent, __assign({ nodeEventName: "onClick", loggerType: "click" }, rest, { children: children })));
}

/**
 * logger를 선언적으로 사용할 수 있는 컴포넌트입니다.
 * 내부적으로 ImpressionArea를 사용하고 있기 때문에 자동으로 onImpressionStart 이벤트 발생 시 로거가 발송됩니다.
 *
 * @example
 * <LoggingImpression
 *  screenName="offer_detail"
 *  eventName="offer_detail"
 *  pageCategory="페이지 이름"
 *  params={{ 서비스: service.name }}
 *  key="product_id"
 * >
 *   <button>구독 취소</button>
 * </LoggingImpression>
 */
function LoggingImpression(_a) {
    var children = _a.children, impressionOptions = _a.impressionOptions, className = _a.className, css = _a.css, rest = __rest(_a, ["children", "impressionOptions", "className", "css"]);
    return (jsx(LoggingEvent, __assign({ nodeEventName: "onImpressionStart", loggerType: "impression", impressionOption: {
            key: impressionOptions.key,
        } }, rest, { children: jsx(ImpressionArea, __assign({ className: className }, impressionOptions, { css: css, children: children })) })));
}

var impression = instance.getPageLogger().impression;
/**
 * logger를 선언적으로 사용할 수 있는 컴포넌트입니다.
 * 내부적으로 ImpressionArea를 사용하고 있기 때문에 자동으로 onImpressionStart 이벤트 발생 시 로거가 발송됩니다.
 *
 * @example
 * <LoggingChargedImpression
 *  screenName="offer_detail"
 *  eventName="offer_detail"
 *  pageCategory="페이지 이름"
 *  params={{ 서비스: service.name }}
 *  chargedImpressionOptions={{
 *    key: 'product_id',
 *    chargeDuration: 1000,
 *    logDelay: 200,
 *  }}
 * >
 *   <div>임프레션 아이템</div>
 * </LoggingChargedImpression>
 */
function LoggingChargedImpression(_a) {
    var children = _a.children, screenName = _a.screenName, eventName = _a.eventName, pageCategory = _a.pageCategory, params = _a.params, chargedImpressionOptions = _a.chargedImpressionOptions, _b = _a.threshold, threshold = _b === void 0 ? 0.5 : _b, _c = _a.delay, delay = _c === void 0 ? 0 : _c, isDisabled = _a.isDisabled;
    var combinedParams = __assign({ screenName: screenName, eventName: eventName, pageCategory: pageCategory }, params);
    var handleImpression = useCallback(function (isImpressed) {
        if (isDisabled)
            return;
        chargedImpressionOptions.key; var chargeOptions = __rest(chargedImpressionOptions, ["key"]);
        impression(chargedImpressionOptions.key, combinedParams, __assign({ isCharging: isImpressed }, chargeOptions));
    }, [chargedImpressionOptions, combinedParams, isDisabled]);
    return (jsx(ImpressionArea, { threshold: threshold, delay: delay, onImpressionStart: function () { return handleImpression(true); }, onImpressionEnd: function () { return handleImpression(false); }, children: children }, chargedImpressionOptions.key));
}

var index = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ImpressionArea: ImpressionArea,
    LoggingClick: LoggingClick,
    LoggingImpression: LoggingImpression,
    LoggingEvent: LoggingEvent,
    LoggingChargedImpression: LoggingChargedImpression,
    useImpression: useImpression,
    useLogConnector: useLogConnector,
    useChargedImpressionLog: useChargedImpressionLog
});

export { sentry, index as weblog };
//# sourceMappingURL=index.esm.js.map
